import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { AuditChip } from '@/components/audit/AuditChip';
import { ShippingZoneForm } from '@/features/commerce-ops/ShippingZoneForm';
import { ShippingRateForm } from '@/features/commerce-ops/ShippingRateForm';
import { TaxRuleForm } from '@/features/commerce-ops/TaxRuleForm';
import {
  useShippingRates,
  useShippingZones,
  useTaxRules,
} from '@/queries/commerceOps';
import { useDesigners } from '@/queries/commerce';
import { useApiError } from '@/lib/useApiError';
import { formatBasisPoints, formatCents } from '@/lib/money';
import { useUiStore } from '@/stores/ui';
import { countryName } from '@/lib/countries';
import type { ShippingRate, ShippingZone, TaxRule } from '@/schemas/admin/commerce/shipping';

export const Route = createFileRoute('/_authed/commerce/shipping')({
  component: ShippingScreen,
});

function ShippingScreen() {
  const { t } = useTranslation('commerce');
  const locale = useUiStore((s) => s.locale);
  const resolveError = useApiError();

  const zones = useShippingZones();
  const rates = useShippingRates();
  const taxRules = useTaxRules();
  const designers = useDesigners();

  const [creatingZone, setCreatingZone] = React.useState(false);
  const [creatingRate, setCreatingRate] = React.useState(false);
  const [creatingTax, setCreatingTax] = React.useState(false);

  const zoneById = React.useMemo(() => {
    const map = new Map<string, ShippingZone>();
    for (const z of zones.data ?? []) map.set(z.id, z);
    return map;
  }, [zones.data]);

  const designerById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const d of designers.data ?? []) map.set(d.id, d.name);
    return map;
  }, [designers.data]);

  const zoneColumns: readonly DataColumn<ShippingZone>[] = [
    {
      id: 'name',
      header: t('shipping.zones.table.name'),
      cell: (z) => (
        <div className="flex flex-col">
          <span className="font-medium">{z.name_en}</span>
          {z.name_ar ? (
            <span dir="rtl" className="text-xs text-[color:var(--color-fg-muted)]">{z.name_ar}</span>
          ) : null}
        </div>
      ),
      sortBy: (z) => z.name_en,
    },
    {
      id: 'countries',
      header: t('shipping.zones.table.countries'),
      cell: (z) =>
        z.countries.length === 0 ? (
          '—'
        ) : (
          <div className="flex flex-wrap gap-1">
            {z.countries.slice(0, 6).map((c) => (
              <Badge key={c} tone="neutral">
                <span dir="ltr" className="font-mono">{c}</span>
                <span className="text-[10px] text-[color:var(--color-fg-muted)]">
                  {countryName(c, locale)}
                </span>
              </Badge>
            ))}
            {z.countries.length > 6 ? (
              <span className="text-xs text-[color:var(--color-fg-muted)]">
                +{z.countries.length - 6}
              </span>
            ) : null}
          </div>
        ),
    },
    {
      id: 'designer',
      header: t('shipping.zones.table.designer'),
      cell: (z) =>
        z.designer_id
          ? designerById.get(z.designer_id) ?? z.designer_id
          : t('shipping.zones.form.designerAll'),
    },
  ];

  const rateColumns: readonly DataColumn<ShippingRate>[] = [
    {
      id: 'zone',
      header: t('shipping.rates.table.zone'),
      cell: (r) => zoneById.get(r.zone_id)?.name_en ?? r.zone_id,
      sortBy: (r) => zoneById.get(r.zone_id)?.name_en ?? '',
    },
    {
      id: 'rate',
      header: t('shipping.rates.table.rate'),
      align: 'end',
      cell: (r) => (
        <span dir="ltr" className="font-mono">
          {formatCents(r.rate_cents, locale, r.currency)}
        </span>
      ),
      sortBy: (r) => r.rate_cents,
    },
    {
      id: 'minMax',
      header: t('shipping.rates.table.minMax'),
      cell: (r) => {
        const min = typeof r.min_cart_cents === 'number' ? formatCents(r.min_cart_cents, locale, r.currency) : '—';
        const max = typeof r.max_cart_cents === 'number' ? formatCents(r.max_cart_cents, locale, r.currency) : '—';
        return (
          <span dir="ltr" className="font-mono text-xs">
            {min} → {max}
          </span>
        );
      },
    },
    {
      id: 'label',
      header: t('shipping.rates.table.label'),
      cell: (r) => r.carrier_label_en ?? '—',
    },
  ];

  const taxColumns: readonly DataColumn<TaxRule>[] = [
    {
      id: 'country',
      header: t('shipping.tax.table.country'),
      cell: (r) => (
        <span>
          <span dir="ltr" className="font-mono me-2">{r.country}</span>
          <span className="text-[color:var(--color-fg-muted)]">{countryName(r.country, locale)}</span>
        </span>
      ),
      sortBy: (r) => r.country,
    },
    {
      id: 'region',
      header: t('shipping.tax.table.region'),
      cell: (r) => r.region ?? '—',
    },
    {
      id: 'rate',
      header: t('shipping.tax.table.rate'),
      align: 'end',
      cell: (r) => <span dir="ltr">{formatBasisPoints(r.rate_bps, locale)}</span>,
      sortBy: (r) => r.rate_bps,
    },
    {
      id: 'label',
      header: t('shipping.tax.table.label'),
      cell: (r) => r.label_en ?? '—',
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('shipping.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('shipping.subtitle')}</p>
        </div>
        <AuditChip />
      </header>

      <Tabs defaultValue="zones">
        <TabsList>
          <TabsTrigger value="zones">{t('shipping.tabs.zones')}</TabsTrigger>
          <TabsTrigger value="rates">{t('shipping.tabs.rates')}</TabsTrigger>
          <TabsTrigger value="tax">{t('shipping.tabs.tax')}</TabsTrigger>
        </TabsList>

        <TabsContent value="zones">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold">{t('shipping.zones.title')}</h2>
                  <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
                    {t('shipping.zones.subtitle')}
                  </p>
                </div>
                <Button onClick={() => setCreatingZone(true)}>
                  <Plus aria-hidden className="size-4" />
                  {t('shipping.zones.new')}
                </Button>
              </div>
              <DataTable<ShippingZone>
                data={zones.data ?? []}
                columns={zoneColumns}
                rowKey={(z) => z.id}
                loading={zones.isLoading}
                error={zones.error ? resolveError(zones.error) : null}
                empty={{
                  title: t('shipping.zones.empty.title'),
                  body: t('shipping.zones.empty.body'),
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rates">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold">{t('shipping.rates.title')}</h2>
                  <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
                    {t('shipping.rates.subtitle')}
                  </p>
                </div>
                <Button onClick={() => setCreatingRate(true)}>
                  <Plus aria-hidden className="size-4" />
                  {t('shipping.rates.new')}
                </Button>
              </div>
              <DataTable<ShippingRate>
                data={rates.data ?? []}
                columns={rateColumns}
                rowKey={(r) => r.id}
                loading={rates.isLoading}
                error={rates.error ? resolveError(rates.error) : null}
                empty={{
                  title: t('shipping.rates.empty.title'),
                  body: t('shipping.rates.empty.body'),
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tax">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold">{t('shipping.tax.title')}</h2>
                  <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">
                    {t('shipping.tax.subtitle')}
                  </p>
                </div>
                <Button onClick={() => setCreatingTax(true)}>
                  <Plus aria-hidden className="size-4" />
                  {t('shipping.tax.form.submit')}
                </Button>
              </div>
              <DataTable<TaxRule>
                data={taxRules.data ?? []}
                columns={taxColumns}
                rowKey={(r) => `${r.country}-${r.region ?? ''}`}
                loading={taxRules.isLoading}
                error={taxRules.error ? resolveError(taxRules.error) : null}
                empty={{
                  title: t('shipping.tax.empty.title'),
                  body: t('shipping.tax.empty.body'),
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={creatingZone} onOpenChange={setCreatingZone}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('shipping.zones.new')}</DialogTitle>
            <DialogDescription>
              {t('shipping.zones.newBody', {
                defaultValue:
                  'Group countries into a shipping zone. Zones can be global or scoped to one designer.',
              })}
            </DialogDescription>
          </DialogHeader>
          <ShippingZoneForm
            onSaved={() => setCreatingZone(false)}
            onCancel={() => setCreatingZone(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={creatingRate} onOpenChange={setCreatingRate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('shipping.rates.new')}</DialogTitle>
            <DialogDescription>
              {t('shipping.rates.newBody', {
                defaultValue:
                  'Set a flat rate for a zone, optionally bounded by cart subtotal.',
              })}
            </DialogDescription>
          </DialogHeader>
          <ShippingRateForm
            onSaved={() => setCreatingRate(false)}
            onCancel={() => setCreatingRate(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={creatingTax} onOpenChange={setCreatingTax}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('shipping.tax.title')}</DialogTitle>
            <DialogDescription>
              {t('shipping.tax.newBody', {
                defaultValue:
                  'Set tax rates per country (and optional region) in basis points.',
              })}
            </DialogDescription>
          </DialogHeader>
          <TaxRuleForm
            onSaved={() => setCreatingTax(false)}
            onCancel={() => setCreatingTax(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
